<template>
  <TwoColumnFormCard>
    <h2 class="mb-4 text-xl">{{ t('dashboard.jobAlert.created') }}</h2>
    <div
      v-if="dashboardStore.isLoading"
      class="flex h-93 items-center justify-center"
    >
      <LoadingSpinner class="h-18 w-18 text-indigo-400" />
    </div>
    <template v-else>
      <p class="mb-2 text-sm">
        <span class="text-gray-500"
          >{{ totalJobAlerts }} {{ t('dashboard.total') }},
        </span>
        <span
          :class="{
            'text-green-600': jobAlertDifference >= 0,
            'text-red-600': jobAlertDifference < 0,
          }"
          >{{ jobAlertDifference >= 0 ? '+' : '' }}{{ jobAlertDifference }}%
        </span>
        <span class="text-gray-500">{{ t('dashboard.difference') }}</span>
      </p>
      <TimeSeriesChart
        class="h-86"
        :datasets="createdJobAlertsData"
        :options="creationChartOptions"
      />
    </template>
  </TwoColumnFormCard>

  <div class="mt-8 grid grid-cols-2 gap-8">
    <TwoColumnFormCard>
      <h2 class="mb-6 flex items-center gap-x-2 text-xl">
        {{ t('dashboard.jobAlert.categories') }}
      </h2>
      <div
        v-if="dashboardStore.isLoading"
        class="flex h-82 items-center justify-center"
      >
        <LoadingSpinner class="h-18 w-18 text-indigo-400" />
      </div>
      <template v-else>
        <BarChart
          :dataset="jobAlertCategoriesData"
          :options="categoryChartOptions"
          class="h-72"
        />
        <p class="mt-2 text-xs text-gray-500">
          * {{ t('dashboard.jobAlert.categoriesHint') }}
        </p>
      </template>
    </TwoColumnFormCard>

    <TwoColumnFormCard>
      <h2 class="mb-4 text-xl">
        {{ t('dashboard.jobAlert.educations') }}
      </h2>
      <div
        v-if="dashboardStore.isLoading"
        class="flex h-82 items-center justify-center"
      >
        <LoadingSpinner class="h-18 w-18 text-indigo-400" />
      </div>
      <template v-else>
        <BarChart
          :dataset="educationLevelsData"
          :options="educationChartOptions"
          :sortData="true"
          class="h-72"
        />
        <p class="mt-2 text-xs text-gray-500">
          * {{ t('dashboard.jobAlert.educationsHint') }}
        </p>
      </template>
    </TwoColumnFormCard>

    <TwoColumnFormCard>
      <h2 class="mb-4 text-xl">{{ t('dashboard.jobAlert.regions') }}</h2>
      <div
        v-if="dashboardStore.isLoading"
        class="flex h-155 items-center justify-center"
      >
        <LoadingSpinner class="h-18 w-18 text-indigo-400" />
      </div>
      <template v-else>
        <p v-if="jobAlertsWithoutRegion" class="text-xs text-gray-500">
          * {{ jobAlertsWithoutRegion }}
        </p>
        <MapChart :dataset="regionsJobAlertsData" />
      </template>
    </TwoColumnFormCard>

    <div>
      <TwoColumnFormCard>
        <h2 class="mb-4 text-xl">
          {{ t('dashboard.jobAlert.disciplines') }}
        </h2>
        <div
          v-if="dashboardStore.isLoading"
          class="flex h-82 items-center justify-center"
        >
          <LoadingSpinner class="h-18 w-18 text-indigo-400" />
        </div>
        <template v-else>
          <BarChart
            :dataset="disciplinesData"
            :sortData="true"
            :options="disciplineChartOptions"
            class="h-72"
          />
          <p class="mt-2 text-xs text-gray-500">
            * {{ t('dashboard.jobAlert.disciplinesHint') }}
          </p>
        </template>
      </TwoColumnFormCard>
    </div>
  </div>
</template>

<script setup>
import {
  disciplineCategories,
  useDashboardStore,
} from '../../store/dashboardStore'
import { computed, ref } from 'vue'
import TwoColumnFormCard from '../TwoColumnFormCard.vue'
import { useI18n } from 'vue-i18n'
import TimeSeriesChart from './TimeSeriesChart.vue'
import MapChart from './MapChart.vue'
import BarChart from './BarChart.vue'
import LoadingSpinner from '../LoadingSpinner.vue'
import { addMonths, format, subMonths } from 'date-fns'

const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global',
})

const dashboardStore = useDashboardStore()

const createdJobAlertsData = ref([])
const regionsJobAlertsData = ref({})
const educationLevelsData = ref({})
const disciplinesData = ref({})
const jobAlertCategoriesData = ref({})

const totalJobAlerts = ref(0)
const jobAlertDifference = ref(0)

const jobAlertsWithoutRegion = computed(() => {
  if (regionsJobAlertsData.value?.data) {
    const unlocalizedJobAlerts = regionsJobAlertsData.value.data['not-set']
    if (unlocalizedJobAlerts) {
      const unlocalizedPercentage = (
        (unlocalizedJobAlerts / totalJobAlerts.value) *
        100
      ).toFixed(1)

      return `${unlocalizedPercentage}% of job alerts don't include a location (${unlocalizedJobAlerts})`
    }
  }
  return ''
})

const creationChartOptions = computed(() => ({
  plugins: {
    // legend: {
    //   display: true,
    // },
    // tooltip: {
    //   callbacks: {
    //     label(context) {
    //       const label = context.dataset.label
    //         ? `${context.dataset.label}: `
    //         : ''
    //       let dateSuffix = ''
    //       if (
    //         context.dataset.label === t('dashboard.jobAlert.previousPeriod')
    //       ) {
    //         const date = context.chart.data.labels[context.dataIndex]
    //         const previousPeriodDateStr = format(
    //           subMonths(date, 1),
    //           'EEEE, dd-MM-yyyy',
    //         )
    //         dateSuffix = ` (${previousPeriodDateStr})`
    //       }
    //       return `${label}${context.formattedValue}${dateSuffix}`
    //     },
    //   },
    // },
  },
}))

const categoryChartOptions = computed(() => ({
  indexAxis: 'y',
  scales: { x: { max: totalJobAlerts.value } },
  plugins: {
    datalabels: {
      anchor: 'end',
      align: 'right',
      offset: 4,
      clip: false,
      formatter: (value) =>
        totalJobAlerts.value
          ? `${Math.round(((value * 10) / totalJobAlerts.value) * 100) / 10}%`
          : '0%',
    },
  },
}))

const educationChartOptions = computed(() => ({
  indexAxis: 'y',
  scales: { x: { max: totalJobAlerts.value } },
  plugins: {
    datalabels: {
      anchor: 'end',
      align: 'right',
      offset: 4,
      clip: false,
      formatter: (value) =>
        totalJobAlerts.value
          ? `${Math.round(((value * 10) / totalJobAlerts.value) * 100) / 10}%`
          : '0%',
    },
  },
}))

const disciplineChartOptions = computed(() => ({
  indexAxis: 'y',
  scales: { x: { max: totalJobAlerts.value } },
  plugins: {
    datalabels: {
      anchor: 'end',
      align: 'right',
      offset: 4,
      clip: false,
      formatter: (value) =>
        totalJobAlerts.value
          ? `${Math.round(((value * 10) / totalJobAlerts.value) * 100) / 10}%`
          : '0%',
    },
    tooltip: {
      footerFont: {
        weight: 'normal',
      },
      callbacks: {
        footer(context) {
          const category = context[0].label
          const relatedDisciplines = Object.keys(disciplineCategories).filter(
            (key) => disciplineCategories[key] === category,
          )
          if (relatedDisciplines.length) {
            return `${t('dashboard.disciplines')}:\n- ${relatedDisciplines.join('\n- ')}`
          }
          return ''
        },
      },
    },
  },
}))

dashboardStore
  .fetchJobAlerts()
  .then(
    ({
      currentPeriod,
      previousPeriod,
      regions,
      disciplines,
      educationLevels,
      categories,
      totalCurrent,
      totalPrevious,
    }) => {
      createdJobAlertsData.value = [
        {
          label: t('dashboard.jobAlert.created'), //t('dashboard.jobAlert.currentPeriod'),
          data: currentPeriod,
        },
        // {
        //   label: t('dashboard.jobAlert.previousPeriod'),
        //   data: Object.entries(previousPeriod).reduce((acc, [date, value]) => {
        //     const shiftedDate = format(addMonths(date, 1), 'yyyy-MM-dd')
        //     acc[shiftedDate] = value
        //     return acc
        //   }, {}),
        //   hidden: true,
        //   backgroundColor: 'oklch(64.6% 0.222 41.116)', // orange-600
        //   borderColor: 'oklch(83.7% 0.128 66.29)', // orange-300
        // },
      ]
      regionsJobAlertsData.value = {
        label: t('dashboard.jobAlert.created'),
        data: regions,
      }
      educationLevelsData.value = {
        label: t('dashboard.jobAlert.created'),
        data: educationLevels,
      }
      disciplinesData.value = {
        label: t('dashboard.jobAlert.created'),
        data: disciplines,
      }
      jobAlertCategoriesData.value = {
        label: t('dashboard.jobAlert.created'),
        data: Object.entries(categories)
          .sort(([, a], [, b]) => b - a)
          .reduce((acc, [label, data]) => {
            const i18NLabel = t(`dashboard.jobAlert.category.${label}`)
            acc[i18NLabel] = data
            return acc
          }, {}),
      }

      const difference = totalCurrent / totalPrevious - 1
      jobAlertDifference.value = Math.round(difference * 1000) / 10 // 1 decimal place
      totalJobAlerts.value = totalCurrent
    },
  )
</script>
